import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const accentColor = (result) => {
  return result?.color_scheme?.accent || "#7f5af0";
};

// ---- A4 Print Card (hidden, captured by html2canvas) ----
// A4 portrait at 96dpi = 794×1123px; white background for printing

const A4PrintCard = ({ result, avatarUrl, accent }) => {
  const traits = (result?.core_traits || result?.traits || []).slice(0, 3);
  const archetype = result?.personality_archetype || result?.archetype || "";
  const numerology = result?.numerology;

  const mulank = numerology?.mulank;
  const expression = numerology?.expression;
  const soulUrge = numerology?.soul_urge;
  const combinedReading = numerology?.combined_reading;

  // Print-friendly palette
  const dark = "#111827";       // near-black text
  const mid = "#374151";        // secondary text
  const muted = "#6b7280";      // muted text
  const border = "#e5e7eb";     // light border
  const bg = "#f9fafb";         // section background
  const accentDark = accent;    // keep accent for highlights/badges

  return (
    <div
      style={{
        width: "794px",
        minHeight: "1123px",
        background: "#ffffff",
        fontFamily: "'Rajdhani', 'Segoe UI', sans-serif",
        color: dark,
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── HEADER ── */}
      <div style={{
        flexShrink: 0,
        background: "#ffffff",
        borderBottom: `3px solid ${accentDark}`,
        padding: "14px 36px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        {/* Scrobits logo */}
        <img
          src="/Scrobits_logo_black.png"
          alt="Scrobits"
          style={{ height: "32px", objectFit: "contain", display: "block" }}
          crossOrigin="anonymous"
        />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "13px", letterSpacing: "4px", color: accentDark, textTransform: "uppercase", fontWeight: "700" }}>HeroScan</div>
          <div style={{ fontSize: "9px", letterSpacing: "2px", color: muted, textTransform: "uppercase", marginTop: "2px" }}>AI Personality Analysis</div>
        </div>
      </div>

      {/* ── HERO IMAGE — full width, fixed height ── */}
      <div style={{ flexShrink: 0, position: "relative", height: "380px", overflow: "hidden", background: "#f3f4f6" }}>
        {avatarUrl ? (
          <>
            <img
              src={avatarUrl}
              alt="Hero Avatar"
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }}
            />
            {/* Fade to white at bottom */}
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: "120px",
              background: "linear-gradient(to bottom, transparent, #ffffff)",
              pointerEvents: "none",
            }} />
          </>
        ) : (
          <div style={{
            width: "100%", height: "100%",
            background: `linear-gradient(135deg, #f3f4f6, ${accentDark}18)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            borderBottom: `1px solid ${border}`,
          }}>
            <span style={{ fontSize: "80px", opacity: 0.2 }}>⚡</span>
          </div>
        )}
      </div>

      {/* ── IDENTITY ── */}
      <div style={{ flexShrink: 0, padding: "6px 40px 18px", position: "relative", zIndex: 2 }}>
        {/* Archetype badge */}
        <div style={{
          display: "inline-flex", alignItems: "center",
          background: `${accentDark}12`,
          border: `1px solid ${accentDark}`,
          color: accentDark,
          padding: "3px 12px",
          borderRadius: "3px",
          fontSize: "9px",
          fontFamily: "'Orbitron', monospace",
          letterSpacing: "2px",
          textTransform: "uppercase",
          marginBottom: "8px",
          lineHeight: "1.4",
        }}>
          {archetype || "Hero"}
        </div>

        {/* Hero title */}
        <div style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: "34px",
          fontWeight: "900",
          letterSpacing: "3px",
          textTransform: "uppercase",
          lineHeight: "1.1",
          color: dark,
          marginBottom: "6px",
        }}>
          {result?.superhero_title || "Hero"}
        </div>

        {/* One-liner quote */}
        <div style={{
          fontSize: "14px",
          color: mid,
          fontStyle: "italic",
          lineHeight: "1.5",
          marginBottom: "12px",
        }}>
          "{result?.one_liner || "A hero rises"}"
        </div>

        {/* Core traits — pills */}
        {traits.length > 0 && (
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {traits.map((t, i) => (
              <span key={i} style={{
                background: `${accentDark}10`,
                border: `1px solid ${accentDark}40`,
                color: mid,
                padding: "3px 12px",
                borderRadius: "20px",
                fontSize: "11px",
                letterSpacing: "0.5px",
                lineHeight: "1.5",
              }}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── DIVIDER ── */}
      <div style={{ height: "1px", background: border, margin: "0 40px", flexShrink: 0 }} />

      {/* ── SUPERPOWER + KRYPTONITE ── */}
      <div style={{ flexShrink: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", padding: "16px 40px" }}>
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", padding: "14px 16px" }}>
          <div style={{ color: "#16a34a", fontSize: "9px", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "8px", fontFamily: "'Orbitron', monospace", lineHeight: "1.4" }}>
            ⚡ Superpower
          </div>
          <div style={{ fontSize: "13px", color: dark, lineHeight: "1.6" }}>
            {result?.superpower || "—"}
          </div>
        </div>
        <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: "6px", padding: "14px 16px" }}>
          <div style={{ color: "#dc2626", fontSize: "9px", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "8px", fontFamily: "'Orbitron', monospace", lineHeight: "1.4" }}>
            ⚠ Kryptonite
          </div>
          <div style={{ fontSize: "13px", color: dark, lineHeight: "1.6" }}>
            {result?.kryptonite || "—"}
          </div>
        </div>
      </div>

      {/* ── NUMEROLOGY ── */}
      {numerology && (
        <>
          <div style={{ height: "1px", background: border, margin: "0 40px", flexShrink: 0 }} />
          <div style={{ flexShrink: 0, padding: "14px 40px 0" }}>
            <div style={{ color: muted, fontSize: "8px", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "10px", fontFamily: "'Orbitron', monospace" }}>
              Numerology
            </div>

            {mulank && (
              <>
                {/* Mulank featured card */}
                <div style={{
                  background: bg,
                  border: `1.5px solid ${accentDark}`,
                  borderRadius: "8px", padding: "14px 16px", marginBottom: "10px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
                    <div style={{
                      width: "44px", height: "44px", borderRadius: "8px",
                      background: accentDark,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Orbitron', monospace", fontWeight: "900", fontSize: "22px", color: "#fff",
                      flexShrink: 0,
                    }}>
                      {mulank.number}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ color: muted, fontSize: "8px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace" }}>Mulank · Birth Day Number</div>
                        {mulank.planet_short && (
                          <span style={{ color: accentDark, fontSize: "9px", background: `${accentDark}12`, border: `1px solid ${accentDark}44`, padding: "1px 7px", borderRadius: "10px", lineHeight: "1.6" }}>
                            {mulank.planet_short}
                          </span>
                        )}
                      </div>
                      {mulank.title && (
                        <div style={{ color: dark, fontSize: "14px", fontWeight: "700", marginTop: "2px" }}>{mulank.title}</div>
                      )}
                    </div>
                  </div>

                  {mulank.core_qualities && mulank.core_qualities.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: "10px" }}>
                      {mulank.core_qualities.map((q, i) => (
                        <span key={i} style={{
                          background: `${accentDark}10`, border: `1px solid ${accentDark}30`,
                          color: mid, padding: "2px 9px", borderRadius: "20px", fontSize: "10px", lineHeight: "1.6",
                        }}>{q}</span>
                      ))}
                    </div>
                  )}

                  {mulank.gift && (
                    <div style={{ marginBottom: "7px" }}>
                      <span style={{ color: "#16a34a", fontSize: "8px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", marginRight: "6px" }}>Your Gift</span>
                      <span style={{ fontSize: "11px", color: mid, lineHeight: "1.6" }}>{mulank.gift}</span>
                    </div>
                  )}

                  {mulank.shadow && (
                    <div>
                      <span style={{ color: "#dc2626", fontSize: "8px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", marginRight: "6px" }}>Watch Out For</span>
                      <span style={{ fontSize: "11px", color: mid, lineHeight: "1.6" }}>{mulank.shadow}</span>
                    </div>
                  )}

                  {mulank.insight && (
                    <div style={{
                      marginTop: "8px", paddingTop: "8px",
                      borderTop: `1px solid ${border}`,
                      fontSize: "11px", color: muted, fontStyle: "italic", lineHeight: "1.6",
                    }}>
                      {mulank.insight}
                    </div>
                  )}
                </div>

                {/* Expression + Soul Urge */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "10px" }}>
                  {expression && (
                    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: "6px", padding: "10px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                        <div style={{ width: "26px", height: "26px", borderRadius: "4px", background: `${accentDark}15`, border: `1px solid ${accentDark}40`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', monospace", fontWeight: "700", fontSize: "12px", color: accentDark, flexShrink: 0 }}>
                          {expression.number}
                        </div>
                        <div style={{ color: muted, fontSize: "8px", letterSpacing: "1px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace" }}>Expression</div>
                      </div>
                      {expression.insight && <div style={{ fontSize: "10px", color: mid, lineHeight: "1.5" }}>{expression.insight}</div>}
                    </div>
                  )}
                  {soulUrge && (
                    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: "6px", padding: "10px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                        <div style={{ width: "26px", height: "26px", borderRadius: "4px", background: `${accentDark}15`, border: `1px solid ${accentDark}40`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', monospace", fontWeight: "700", fontSize: "12px", color: accentDark, flexShrink: 0 }}>
                          {soulUrge.number}
                        </div>
                        <div style={{ color: muted, fontSize: "8px", letterSpacing: "1px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace" }}>Soul Urge</div>
                      </div>
                      {soulUrge.insight && <div style={{ fontSize: "10px", color: mid, lineHeight: "1.5" }}>{soulUrge.insight}</div>}
                    </div>
                  )}
                </div>

                {combinedReading && (
                  <div style={{ fontSize: "10px", color: muted, fontStyle: "italic", lineHeight: "1.6", paddingBottom: "10px" }}>
                    {combinedReading}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── FOOTER — Scrobits branding ── */}
      <div style={{
        marginTop: "auto",
        background: "#f9fafb",
        borderTop: `2px solid ${accentDark}`,
        padding: "12px 36px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <img
          src="/Scrobits_logo_black.png"
          alt="Scrobits"
          style={{ height: "22px", objectFit: "contain", display: "block", opacity: 0.75 }}
          crossOrigin="anonymous"
        />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "9px", color: muted, letterSpacing: "0.5px" }}>Powered by HeroScan · AI Personality Analysis</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "10px", color: mid, letterSpacing: "0.3px" }}>contact@scrobits.com</div>
          <div style={{ fontSize: "10px", color: accentDark, letterSpacing: "0.3px" }}>www.scrobits.com</div>
        </div>
      </div>
    </div>
  );
};

// ---- main component ----

const ResultsScreen = ({ result, visitorData, onContinue }) => {
  const [avatarUrl, setAvatarUrl] = useState(result?.avatar_url || null);
  const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isPrintingA4, setIsPrintingA4] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const cardRef = useRef(null);
  const printCardRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsDownloading(true);
    setIsCapturing(true);
    // Let React re-render with buttons hidden before capturing
    await new Promise((r) => setTimeout(r, 80));
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      // Use canvas dimensions as the PDF page size so everything fits on 1 page
      const pxToMm = 0.264583;
      const pageW = canvas.width * pxToMm;
      const pageH = canvas.height * pxToMm;
      const pdf = new jsPDF({ orientation: pageH > pageW ? "portrait" : "landscape", unit: "mm", format: [pageW, pageH] });
      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", 0, 0, pageW, pageH);
      pdf.save(`heroscan-${(result?.superhero_title || "hero").replace(/\s+/g, "-").toLowerCase()}.pdf`);
      toast.success("PDF downloaded!");
    } catch (e) {
      console.error(e);
      toast.error("Download failed, try again");
    } finally {
      setIsCapturing(false);
      setIsDownloading(false);
    }
  };

  const handlePrintA4 = async () => {
    if (!printCardRef.current) return;
    setIsPrintingA4(true);
    try {
      const canvas = await html2canvas(printCardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: 794,
        height: printCardRef.current.scrollHeight,
        windowWidth: 794,
      });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();  // 210mm
      const pageH = pdf.internal.pageSize.getHeight(); // 297mm
      // Scale to fill width; if taller than A4 shrink to fit height instead
      let imgW = pageW;
      let imgH = (canvas.height * pageW) / canvas.width;
      if (imgH > pageH) {
        imgH = pageH;
        imgW = (canvas.width * pageH) / canvas.height;
      }
      const x = (pageW - imgW) / 2;
      const y = (pageH - imgH) / 2;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, y, imgW, imgH);
      pdf.save(`heroscan-a4-${(result?.superhero_title || "hero").replace(/\s+/g, "-").toLowerCase()}.pdf`);
      toast.success("A4 PDF downloaded!");
    } catch (e) {
      console.error(e);
      toast.error("Download failed, try again");
    } finally {
      setIsPrintingA4(false);
    }
  };

  const generateAvatar = async () => {
    if (!result?.id) return;
    setIsGeneratingAvatar(true);
    try {
      const response = await axios.post(`${API}/generate-avatar`, {
        visitor_id: result.visitor_id,
        result_id: result.id
      });
      setAvatarUrl(response.data.avatar_url);
      toast.success("Avatar generated!");
    } catch (error) {
      console.error("Avatar generation failed:", error);
      toast.error("Failed to generate avatar");
    } finally {
      setIsGeneratingAvatar(false);
    }
  };

  const traits = result?.core_traits || result?.traits || [];
  const archetype = result?.personality_archetype || result?.archetype || "";
  const accent = accentColor(result);

  const numerology = result?.numerology;
  const mulank = numerology?.mulank;
  const expression = numerology?.expression;
  const soulUrge = numerology?.soul_urge;

  // Print-friendly palette (same as A4)
  const dark = "#111827";
  const mid = "#374151";
  const muted = "#6b7280";
  const cardBorder = "#e5e7eb";
  const sectionBg = "#f9fafb";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ background: "#f3f4f6", minHeight: "100vh", padding: "24px 16px 40px" }}
      data-testid="results-screen"
    >
      {/* Hidden A4 print card — off-screen, captured by handlePrintA4 */}
      <div style={{ position: "fixed", left: "-9999px", top: 0, zIndex: -1, width: "794px" }}>
        <div ref={printCardRef}>
          <A4PrintCard result={result} avatarUrl={avatarUrl} accent={accent} />
        </div>
      </div>

      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 24 }}
        transition={{ duration: 0.5 }}
        style={{
          maxWidth: "680px",
          margin: "0 auto",
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 4px 32px rgba(0,0,0,0.10)",
          overflow: "hidden",
          fontFamily: "'Rajdhani', 'Segoe UI', sans-serif",
          color: dark,
        }}
      >
        {/* ── HEADER ── */}
        <div style={{
          background: "#fff",
          borderBottom: `3px solid ${accent}`,
          padding: "14px 28px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <img src="/Scrobits_logo_black.png" alt="Scrobits" style={{ height: "28px", objectFit: "contain" }} />
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "12px", letterSpacing: "4px", color: accent, textTransform: "uppercase", fontWeight: "700" }}>HeroScan</div>
            <div style={{ fontSize: "9px", letterSpacing: "2px", color: muted, textTransform: "uppercase", marginTop: "1px" }}>AI Personality Analysis</div>
          </div>
        </div>

        {/* ── HERO IMAGE / AVATAR ── */}
        <div style={{ position: "relative", background: "#f3f4f6", overflow: "hidden" }}>
          {avatarUrl ? (
            <>
              <img
                src={avatarUrl}
                alt="Hero Avatar"
                style={{ width: "100%", height: "420px", objectFit: "cover", objectPosition: "top center", display: "block" }}
                data-testid="hero-avatar"
              />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "120px", background: "linear-gradient(to bottom, transparent, #ffffff)", pointerEvents: "none" }} />
              {/* Regenerate button overlay */}
              <button
                onClick={generateAvatar}
                disabled={isGeneratingAvatar}
                style={{
                  position: "absolute", bottom: "16px", right: "16px",
                  background: "rgba(255,255,255,0.92)", border: `1px solid ${accent}`,
                  color: accent, padding: "6px 14px", borderRadius: "6px",
                  fontSize: "11px", fontFamily: "'Orbitron', monospace", letterSpacing: "1px",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
                  fontWeight: "600",
                }}
              >
                {isGeneratingAvatar ? "Generating..." : "↻ Regenerate"}
              </button>
            </>
          ) : (
            <div style={{
              height: "320px", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: "16px",
              background: `linear-gradient(135deg, #f3f4f6, ${accent}10)`,
              borderBottom: `1px solid ${cardBorder}`,
            }}>
              {isGeneratingAvatar ? (
                <>
                  <div style={{ width: "44px", height: "44px", border: `3px solid ${accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  <span style={{ color: muted, fontSize: "13px", fontFamily: "'Rajdhani', sans-serif", letterSpacing: "1px" }}>Generating your avatar...</span>
                </>
              ) : (
                <>
                  <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: `${accent}15`, border: `2px dashed ${accent}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px" }}>⚡</div>
                  <button
                    onClick={generateAvatar}
                    data-testid="generate-avatar-btn"
                    style={{
                      background: accent, color: "#fff",
                      padding: "10px 28px", borderRadius: "6px",
                      fontSize: "13px", fontFamily: "'Orbitron', monospace",
                      letterSpacing: "2px", textTransform: "uppercase",
                      border: "none", cursor: "pointer", fontWeight: "700",
                    }}
                  >
                    Generate Avatar
                  </button>
                  <span style={{ color: muted, fontSize: "11px" }}>Your superhero portrait awaits</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── IDENTITY ── */}
        <div style={{ padding: "20px 28px 16px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center",
            background: `${accent}12`, border: `1px solid ${accent}`,
            color: accent, padding: "4px 12px", borderRadius: "4px",
            fontSize: "9px", fontFamily: "'Orbitron', monospace",
            letterSpacing: "2px", textTransform: "uppercase",
            marginBottom: "10px", lineHeight: "1.4",
          }}>
            {archetype || "Hero"}
          </div>
          <div style={{
            fontFamily: "'Orbitron', monospace", fontSize: "28px", fontWeight: "900",
            letterSpacing: "3px", textTransform: "uppercase", lineHeight: "1.1",
            color: dark, marginBottom: "8px",
          }}>
            {result?.superhero_title || "Hero"}
          </div>
          <div style={{ fontSize: "14px", color: mid, fontStyle: "italic", lineHeight: "1.6", marginBottom: "12px" }}>
            "{result?.one_liner || "A hero rises"}"
          </div>
          {/* Trait pills */}
          {traits.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
              {traits.map((t, i) => (
                <span key={i} style={{
                  background: `${accent}10`, border: `1px solid ${accent}40`,
                  color: mid, padding: "4px 12px", borderRadius: "20px",
                  fontSize: "11px", lineHeight: "1.5",
                }}>{t}</span>
              ))}
            </div>
          )}
          {/* Color bar */}
          {result?.color_scheme && (
            <div style={{ display: "flex", gap: "4px", height: "4px", borderRadius: "4px", overflow: "hidden", marginTop: "4px" }}>
              {[result.color_scheme.primary, result.color_scheme.secondary, result.color_scheme.accent].map((c, i) => (
                <div key={i} style={{ flex: 1, background: c }} />
              ))}
            </div>
          )}
        </div>

        {/* ── DIVIDER ── */}
        <div style={{ height: "1px", background: cardBorder, margin: "0 28px" }} />

        {/* ── SUPERPOWER + KRYPTONITE ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", padding: "16px 28px" }}>
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
              <span style={{ fontSize: "13px", lineHeight: "1" }}>⚡</span>
              <span style={{ color: "#16a34a", fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", lineHeight: "1.4" }}>Superpower</span>
            </div>
            <p style={{ fontSize: "13px", color: dark, lineHeight: "1.6", margin: 0 }}>{result?.superpower || "—"}</p>
          </div>
          <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: "8px", padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
              <span style={{ fontSize: "13px", lineHeight: "1" }}>⚠</span>
              <span style={{ color: "#dc2626", fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", lineHeight: "1.4" }}>Kryptonite</span>
            </div>
            <p style={{ fontSize: "13px", color: dark, lineHeight: "1.6", margin: 0 }}>{result?.kryptonite || "—"}</p>
          </div>
        </div>

        {/* ── NUMEROLOGY ── */}
        {numerology && (
          <>
            <div style={{ height: "1px", background: cardBorder, margin: "0 28px" }} />
            <div style={{ padding: "16px 28px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                <span style={{ color: muted, fontSize: "8px", letterSpacing: "3px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace" }}>Numerology</span>
              </div>

              {mulank && (
                <>
                  {/* Mulank featured */}
                  <div style={{ background: sectionBg, border: `1.5px solid ${accent}`, borderRadius: "8px", padding: "14px 16px", marginBottom: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
                      <div style={{
                        width: "44px", height: "44px", borderRadius: "8px",
                        background: accent, display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "'Orbitron', monospace", fontWeight: "900", fontSize: "22px", color: "#fff",
                        flexShrink: 0,
                      }}>
                        {mulank.number}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span style={{ color: muted, fontSize: "8px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace" }}>Mulank · Birth Day</span>
                          {mulank.planet_short && (
                            <span style={{ color: accent, fontSize: "9px", background: `${accent}12`, border: `1px solid ${accent}44`, padding: "2px 8px", borderRadius: "10px", lineHeight: "1.6" }}>
                              {mulank.planet_short}
                            </span>
                          )}
                        </div>
                        {mulank.title && <div style={{ color: dark, fontSize: "15px", fontWeight: "700", marginTop: "2px" }}>{mulank.title}</div>}
                      </div>
                    </div>
                    {mulank.core_qualities?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "10px" }}>
                        {mulank.core_qualities.map((q, i) => (
                          <span key={i} style={{ background: `${accent}10`, border: `1px solid ${accent}30`, color: mid, padding: "3px 10px", borderRadius: "20px", fontSize: "11px", lineHeight: "1.5" }}>{q}</span>
                        ))}
                      </div>
                    )}
                    {mulank.gift && (
                      <div style={{ marginBottom: "8px" }}>
                        <span style={{ color: "#16a34a", fontSize: "8px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", marginRight: "6px" }}>Your Gift</span>
                        <span style={{ fontSize: "12px", color: mid, lineHeight: "1.6" }}>{mulank.gift}</span>
                      </div>
                    )}
                    {mulank.shadow && (
                      <div style={{ marginBottom: "8px" }}>
                        <span style={{ color: "#dc2626", fontSize: "8px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", marginRight: "6px" }}>Watch Out For</span>
                        <span style={{ fontSize: "12px", color: mid, lineHeight: "1.6" }}>{mulank.shadow}</span>
                      </div>
                    )}
                    {mulank.insight && (
                      <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: `1px solid ${cardBorder}`, fontSize: "12px", color: muted, fontStyle: "italic", lineHeight: "1.6" }}>
                        {mulank.insight}
                      </div>
                    )}
                  </div>

                  {/* Expression + Soul Urge */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                    {expression && (
                      <div style={{ background: sectionBg, border: `1px solid ${cardBorder}`, borderRadius: "8px", padding: "12px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                          <div style={{ width: "28px", height: "28px", borderRadius: "6px", background: `${accent}15`, border: `1px solid ${accent}40`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', monospace", fontWeight: "700", fontSize: "13px", color: accent, flexShrink: 0 }}>
                            {expression.number}
                          </div>
                          <span style={{ color: muted, fontSize: "8px", letterSpacing: "1px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace" }}>Expression</span>
                        </div>
                        {expression.insight && <div style={{ fontSize: "11px", color: mid, lineHeight: "1.6" }}>{expression.insight}</div>}
                      </div>
                    )}
                    {soulUrge && (
                      <div style={{ background: sectionBg, border: `1px solid ${cardBorder}`, borderRadius: "8px", padding: "12px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                          <div style={{ width: "28px", height: "28px", borderRadius: "6px", background: `${accent}15`, border: `1px solid ${accent}40`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', monospace", fontWeight: "700", fontSize: "13px", color: accent, flexShrink: 0 }}>
                            {soulUrge.number}
                          </div>
                          <span style={{ color: muted, fontSize: "8px", letterSpacing: "1px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace" }}>Soul Urge</span>
                        </div>
                        {soulUrge.insight && <div style={{ fontSize: "11px", color: mid, lineHeight: "1.6" }}>{soulUrge.insight}</div>}
                      </div>
                    )}
                  </div>

                  {numerology.combined_reading && (
                    <div style={{ fontSize: "11px", color: muted, fontStyle: "italic", lineHeight: "1.6" }}>
                      {numerology.combined_reading}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* ── STRENGTHS + GROWTH (if present) ── */}
        {((result?.strengths || []).length > 0 || (result?.growth_edges || []).length > 0) && (
          <>
            <div style={{ height: "1px", background: cardBorder, margin: "0 28px" }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", padding: "16px 28px" }}>
              {(result?.strengths || []).length > 0 && (
                <div style={{ background: sectionBg, border: `1px solid ${cardBorder}`, borderRadius: "8px", padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
                    <span style={{ fontSize: "12px", lineHeight: "1" }}>🛡</span>
                    <span style={{ color: muted, fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", lineHeight: "1.4" }}>Strengths</span>
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {(result.strengths || []).map((s, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px", fontSize: "12px", color: mid, lineHeight: "1.6" }}>
                        <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: accent, flexShrink: 0, marginTop: "7px" }} />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(result?.growth_edges || []).length > 0 && (
                <div style={{ background: sectionBg, border: `1px solid ${cardBorder}`, borderRadius: "8px", padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px" }}>
                    <span style={{ fontSize: "12px", lineHeight: "1" }}>🎯</span>
                    <span style={{ color: muted, fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", lineHeight: "1.4" }}>Growth</span>
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {(result.growth_edges || []).map((e, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px", fontSize: "12px", color: mid, lineHeight: "1.6" }}>
                        <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#0891b2", flexShrink: 0, marginTop: "7px" }} />
                        {e}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── ACTION BUTTONS ── */}
        <div style={{ height: "1px", background: cardBorder, margin: "0 28px", display: isCapturing ? "none" : "block" }} />
        <div style={{ padding: "20px 28px", display: isCapturing ? "none" : "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "11px 22px", borderRadius: "6px",
              background: sectionBg, border: `1.5px solid ${cardBorder}`,
              color: mid, fontSize: "12px",
              fontFamily: "'Orbitron', monospace", letterSpacing: "1.5px",
              textTransform: "uppercase", cursor: "pointer", fontWeight: "600",
            }}
          >
            {isDownloading ? "⏳ Capturing..." : "⬇ Download PDF"}
          </button>
          <button
            onClick={handlePrintA4}
            disabled={isPrintingA4}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "11px 22px", borderRadius: "6px",
              background: sectionBg, border: `1.5px solid ${accent}40`,
              color: accent, fontSize: "12px",
              fontFamily: "'Orbitron', monospace", letterSpacing: "1.5px",
              textTransform: "uppercase", cursor: "pointer", fontWeight: "600",
            }}
          >
            {isPrintingA4 ? "⏳ Preparing..." : "🖨 Print A4"}
          </button>
          <button
            onClick={onContinue}
            data-testid="continue-btn"
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "11px 32px", borderRadius: "6px",
              background: accent, border: "none",
              color: "#fff", fontSize: "12px",
              fontFamily: "'Orbitron', monospace", letterSpacing: "1.5px",
              textTransform: "uppercase", cursor: "pointer", fontWeight: "700",
            }}
          >
            Get Your Results →
          </button>
        </div>

        {/* ── FOOTER ── */}
        <div style={{
          background: sectionBg,
          borderTop: `2px solid ${accent}`,
          padding: "12px 28px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <img src="/Scrobits_logo_black.png" alt="Scrobits" style={{ height: "20px", objectFit: "contain", opacity: 0.7 }} />
          <div style={{ textAlign: "center", fontSize: "9px", color: muted, letterSpacing: "0.5px" }}>
            Powered by Scrobits Technology
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "10px", color: mid }}>contact@scrobits.com</div>
            <div style={{ fontSize: "10px", color: accent }}>www.scrobits.com</div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ResultsScreen;
